import {
  afterRenderEffect,
  Component,
  DestroyRef,
  effect,
  ElementRef,
  inject,
  input,
  output,
  untracked,
  viewChild,
} from '@angular/core';
import { TranslatePipe } from '@ngx-translate/core';
import { ButtonComponent } from '@sb/ui/button';
import { LoadingStateComponent } from '@sb/ui/feedback';

@Component({
  selector: 'requests-paging-status',
  templateUrl: './paging-status.component.html',
  host: { role: 'status', 'aria-live': 'polite', tabindex: '-1' },
  imports: [TranslatePipe, ButtonComponent, LoadingStateComponent],
})
export class PagingStatusComponent {
  hasMore = input(false);
  loading = input(false);
  loadingMore = input(false);
  loadMoreFailed = input(false);
  loadMore = output<void>();
  retry = output<void>();

  #elementRef = inject<ElementRef<HTMLElement>>(ElementRef);
  scrollSentinel = viewChild<ElementRef<HTMLElement>>('scrollSentinel');

  constructor() {
    this.#observeScrollSentinel();
  }

  onRetry(): void {
    this.retry.emit();
    this.#elementRef.nativeElement.focus();
  }

  #observeScrollSentinel(): void {
    if (typeof IntersectionObserver === 'undefined') {
      return;
    }

    const observer = new IntersectionObserver((entries) => {
      if (entries.some((entry) => entry.isIntersecting) && !this.loadMoreFailed()) {
        this.loadMore.emit();
      }
    });

    afterRenderEffect(() => {
      const sentinel = this.scrollSentinel();

      observer.disconnect();

      if (sentinel) {
        observer.observe(sentinel.nativeElement);
      }
    });

    // IntersectionObserver only reports on (re-)observe.
    effect(() => {
      const sentinel = this.scrollSentinel();
      const settled = !this.loading() && !this.loadingMore() && !this.loadMoreFailed();

      if (!sentinel || !settled) {
        return;
      }

      untracked(() => {
        observer.unobserve(sentinel.nativeElement);
        observer.observe(sentinel.nativeElement);
      });
    });

    inject(DestroyRef).onDestroy(() => observer.disconnect());
  }
}
